// Field rules that are specific to the New Employee File (formatting while typing, the
// auto-appended street designation, dates, identification types). The ID number, tax number,
// email, cellphone and bank rules are shared with the other TC Auditors sites - see copied.js.
import {
  validateSAIdNumber,
  validateTaxNumber,
  validateEmail,
  validatePhoneNumber,
  validateAccountNumber,
} from '@/utils/copied';

// SARS IRP5 rules (owner's instruction): plain letters only - NO accented letters - and NO
// full stops in any field (email addresses excepted, they need them).
const L = 'A-Za-z';

// Accented letters are converted to the plain letter as they are typed/pasted (André -> Andre,
// Liné -> Line) rather than silently dropped. Letters with no plain form (ß, æ, ø) are removed
// by each formatter's allowed-character filter.
export const removeAccents = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

// ---------------------------------------------------------------------------
// Formatters - run on every keystroke. Each one only changes letter case or removes
// characters, so formatting the text before the caret gives the new caret position.
// ---------------------------------------------------------------------------

const capitalise = (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

// First Names / Last Name: letters, spaces and hyphens only, Proper Case (incl. after a hyphen).
export const formatPersonName = (raw) =>
  removeAccents(raw)
    .replace(new RegExp(`[^${L} -]`, 'g'), '')
    .replace(/^[\s-]+/, '')
    .replace(/ {2,}/g, ' ')
    .replace(/-{2,}/g, '-')
    .toLowerCase()
    .replace(new RegExp(`(^|[ -])([${L}])`, 'g'), (m, sep, ch) => sep + ch.toUpperCase());

// Job Title: Proper Case, but words of up to 3 letters are UPPER CASE (CEO, CFO, HR, IT, PA).
// Small joining words stay lower case ("Head of Sales and Marketing"). Hyphenated words are
// only capitalised ("Co-Ordinator"), so prefixes like "Co-" / "Sub-" aren't shouted.
const JOB_TITLE_SMALL_WORDS = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'in', 'of', 'on', 'or', 'the', 'to']);

export const formatJobTitle = (raw) => {
  const cleaned = removeAccents(raw)
    .replace(new RegExp(`[^${L}0-9 &/()',:-]`, 'g'), '')
    .replace(/^\s+/, '')
    .replace(/ {2,}/g, ' ');
  let wordIndex = 0;
  // Words are separated by spaces, "/", "&", "(" or ")"
  return cleaned.replace(/[^ /&()]+/g, (token) => {
    const isFirst = wordIndex === 0;
    wordIndex += 1;
    if (token.includes('-')) {
      return token.split('-').map((part) => (/^[a-zA-Z]/.test(part) ? capitalise(part) : part)).join('-');
    }
    const lower = token.toLowerCase();
    const letters = lower.replace(new RegExp(`[^${L}]`, 'g'), '');
    if (!letters) return token; // digits / punctuation only
    if (!isFirst && JOB_TITLE_SMALL_WORDS.has(lower)) return lower;
    if (letters.length <= 3 && !JOB_TITLE_SMALL_WORDS.has(lower)) return token.toUpperCase();
    return capitalise(token);
  });
};

// Name of Employer: Proper Case with acronyms in CAPS, like the Debit Order Mandate's Company
// Name field - words of up to 3 letters are UPPER CASE ("TCA Auditors", "ABC Trading CC"),
// company designations keep their usual form ("(Pty) Ltd", "Inc") and small joining words stay
// lower case ("Smith and Sons"). No accents or full stops (SARS IRP5).
const COMPANY_WORD_CASE = {
  pty: 'Pty', ltd: 'Ltd', inc: 'Inc', co: 'Co',
  // Surname particles and titles are short words but not acronyms ("Van Der Merwe Boerdery", "Mr J Smith")
  van: 'Van', der: 'Der', den: 'Den', de: 'De', du: 'Du', le: 'Le', la: 'La', von: 'Von', del: 'Del',
  mr: 'Mr', mrs: 'Mrs', ms: 'Ms', dr: 'Dr', jnr: 'Jnr', snr: 'Snr',
};

export const formatEmployerName = (raw) => {
  const cleaned = removeAccents(raw)
    .replace(new RegExp(`[^${L}0-9 &()'/,-]`, 'g'), '')
    .replace(/^\s+/, '')
    .replace(/ {2,}/g, ' ');
  let wordIndex = 0;
  // Words are separated by spaces, "/", "&", "(" or ")"
  return cleaned.replace(/[^ /&()]+/g, (token) => {
    const isFirst = wordIndex === 0;
    wordIndex += 1;
    const lower = token.toLowerCase();
    if (COMPANY_WORD_CASE[lower]) return COMPANY_WORD_CASE[lower];
    if (token.includes('-')) {
      return token.split('-').map((part) => (/^[a-zA-Z]/.test(part) ? capitalise(part) : part)).join('-');
    }
    const letters = lower.replace(new RegExp(`[^${L}]`, 'g'), '');
    if (!letters) return token; // digits / punctuation only
    if (!isFirst && JOB_TITLE_SMALL_WORDS.has(lower)) return lower;
    if (letters.length <= 3 && !lower.includes("'") && !JOB_TITLE_SMALL_WORDS.has(lower)) return token.toUpperCase();
    return capitalise(token);
  });
};

// Address fields: Proper Case each word; a word containing a digit is upper-cased ("12b" -> "12B").
export const formatAddress = (raw) =>
  removeAccents(raw)
    .replace(new RegExp(`[^${L}0-9 ',/-]`, 'g'), '')
    .replace(/^\s+/, '')
    .replace(/ {2,}/g, ' ')
    .replace(/[^ ]+/g, (word) =>
      /\d/.test(word)
        ? word.toUpperCase()
        : word.toLowerCase().replace(new RegExp(`(^|-)([${L}])`, 'g'), (m, sep, ch) => sep + ch.toUpperCase())
    );

export const formatDigits = (max) => (raw) => raw.replace(/\D/g, '').slice(0, max);

// Cellphone: digits only, max 10. A pasted international number (+27 82 123 4567) becomes 0821234567.
export const formatCellphone = (raw) => {
  let digits = raw.replace(/\D/g, '');
  if (/^27\d{9}$/.test(digits)) digits = `0${digits.slice(2)}`;
  return digits.slice(0, 10);
};

export const formatPassport = (raw) => raw.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 15);
export const formatAsylumPermit = (raw) => raw.toUpperCase().replace(/[^A-Z0-9/-]/g, '').slice(0, 20);

// ---------------------------------------------------------------------------
// Street name, finalised when the field loses focus:
//  1. an abbreviated street type at the END is written out in full ("Main Rd" -> "Main Road",
//     "Oak Ave" -> "Oak Avenue", "Church St" / "Church Str" -> "Church Street");
//  2. if there is still no street type at the end, " Street" is added ("Church" -> "Church Street"),
//     and taken off again while the field is being edited.
// Only the LAST word is expanded, so e.g. "St Andrews Road" (Saint) is left alone.
// (Full stops can't be typed at all - SARS IRP5 rule - so "Ave." arrives here as "Ave".)
// ---------------------------------------------------------------------------
export const STREET_ABBREVIATIONS = {
  // Street / Road / Avenue / Drive
  st: 'Street', str: 'Street', strt: 'Street', stt: 'Street',
  rd: 'Road',
  ave: 'Avenue', av: 'Avenue', avn: 'Avenue', aven: 'Avenue', avenu: 'Avenue',
  dr: 'Drive', drv: 'Drive', drve: 'Drive',
  // Other English street types
  cres: 'Crescent', cr: 'Crescent', crs: 'Crescent', crsnt: 'Crescent', cresc: 'Crescent',
  cl: 'Close', cls: 'Close',
  ln: 'Lane',
  pl: 'Place',
  blvd: 'Boulevard', bvd: 'Boulevard', boul: 'Boulevard',
  hwy: 'Highway', hway: 'Highway',
  fwy: 'Freeway',
  expy: 'Expressway',
  mwy: 'Motorway',
  ct: 'Court', crt: 'Court',
  cir: 'Circle', circ: 'Circle',
  sq: 'Square', sqr: 'Square',
  tce: 'Terrace', ter: 'Terrace', terr: 'Terrace',
  gr: 'Grove', grv: 'Grove',
  gdns: 'Gardens', gdn: 'Gardens',
  pde: 'Parade',
  cct: 'Circuit',
  esp: 'Esplanade',
  prom: 'Promenade',
  wy: 'Way',
  wk: 'Walk',
  hts: 'Heights',
  rdg: 'Ridge',
  pkwy: 'Parkway',
};

const STREET_DESIGNATIONS = new Set([
  'street', 'road', 'avenue', 'drive', 'crescent', 'close', 'lane', 'way', 'place', 'boulevard',
  'highway', 'freeway', 'expressway', 'motorway', 'bypass', 'court', 'circle', 'circus', 'square', 'terrace',
  'walk', 'walkway', 'row', 'mews', 'grove', 'gardens', 'rise', 'view', 'loop', 'link', 'parade', 'esplanade',
  'promenade', 'path', 'pathway', 'track', 'trail', 'circuit', 'bend', 'alley', 'arcade', 'quay', 'wharf',
  'gate', 'green', 'common', 'ridge', 'heights', 'vale', 'glen', 'dale', 'end', 'approach', 'concourse',
  'corner', 'crossing', 'turn', 'serviceway', 'mall', 'parkway',
  // Afrikaans
  'straat', 'weg', 'laan', 'rylaan', 'singel', 'pad', 'plein', 'hof', 'steeg', 'oord', 'kring', 'draai', 'rand',
]);
// Afrikaans compound names: "Kerkstraat", "Voortrekkerweg", "Pretorialaan", "Parksingel"
const AFRIKAANS_COMPOUND_RE = /(straat|weg|laan|singel|rylaan)$/i;

export const hasStreetDesignation = (street) => {
  const words = (street || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return false;
  const last = words[words.length - 1];
  return STREET_DESIGNATIONS.has(last) || (last.length > 6 && AFRIKAANS_COMPOUND_RE.test(last));
};

// True when the text is ONLY a street type ("Rd", "Road") with no street name.
export const isOnlyStreetType = (street) => {
  const words = (street || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  return words.length === 1 && (STREET_DESIGNATIONS.has(words[0]) || Boolean(STREET_ABBREVIATIONS[words[0]]));
};

// Writes out an abbreviated street type in the last word ("Main Rd" -> "Main Road") and drops
// a doubled street type ("Main Street St" -> "Main Street"). A street type on its own (e.g. just
// "Rd") is left as typed - validation then asks for the street name.
export const expandStreetAbbreviation = (street) => {
  const words = (street || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return words.join(' ');
  const full = STREET_ABBREVIATIONS[words[words.length - 1].toLowerCase().replace(/[.,]/g, '')];
  if (full) words[words.length - 1] = full;
  if (words.length >= 3 && words[words.length - 1].toLowerCase() === words[words.length - 2].toLowerCase()) {
    words.pop();
  }
  return words.join(' ');
};

export const AUTO_STREET_SUFFIX = ' Street';

// ---------------------------------------------------------------------------
// Dates (dd/mm/yyyy)
// ---------------------------------------------------------------------------
export const formatDate = (date) =>
  `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

export const parseDate = (value) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || '');
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 ? d : null;
};

// No minimum age (owner's instruction): clients add newborn children to the payroll, so any
// date of birth up to today is accepted.
const today = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
export const DOB_FROM = new Date(1920, 0, 1);
export const dobTo = () => today();
export const APPOINTMENT_FROM = new Date(1980, 0, 1);
export const appointmentTo = () => { const d = today(); d.setFullYear(d.getFullYear() + 1); return d; };

// ---------------------------------------------------------------------------
// Identification types
// ---------------------------------------------------------------------------
export const ID_TYPES = {
  RSA_ID: 'RSA ID',
  PASSPORT: 'Passport/Foreign ID',
  ASYLUM: "Asylum Seeker's Permit",
  REFUGEE: 'Refugee ID',
};

// RSA ID and Refugee ID are 13-digit, SA-format ID numbers; the other two are document numbers.
export const idTypeUsesSaIdNumber = (idType) => idType === ID_TYPES.RSA_ID || idType === ID_TYPES.REFUGEE;
export const idNumberLabel = (idType) => (idTypeUsesSaIdNumber(idType) ? 'ID Number' : 'Passport/Foreign ID Number');

// ---------------------------------------------------------------------------
// Dropdown options
// ---------------------------------------------------------------------------
export const PAY_FREQUENCIES = ['Monthly', 'Weekly', 'Fortnightly'];
export const ACCOUNT_TYPES = ['Current / Cheque', 'Savings', 'Transmission'];
// Holder Relationship is locked to "Own" (owner's instruction - employees may not change it)
export const HOLDER_RELATIONSHIP = 'Own';

// Unit Number / Street Number: a number below 10 gets a leading 0 when the field loses focus
// ("1" -> "01", "4B" -> "04B", "Unit 4" -> "Unit 04", "4-6" -> "04-06"). Not applied while
// typing, otherwise typing "12" would give "012".
export const padSingleDigits = (value) =>
  value.replace(/(^|\D)([1-9])(?!\d)/g, (match, before, digit) => `${before}0${digit}`);

// ---------------------------------------------------------------------------
// Remuneration Package
// ---------------------------------------------------------------------------
export const UIF_PAYE_OPTIONS = ['Yes', 'No'];

// Gross Salary is typed as a plain number (digits + one decimal separator, at most 2 decimals);
// a decimal comma is accepted as a decimal point. A pasted "R25,000.00" keeps its value.
export const formatAmountInput = (raw) => {
  let s = raw.replace(/[^\d.,]/g, '');
  if (s.includes('.')) s = s.replace(/,/g, '');
  s = s.replace(/,/g, '.');
  const [whole, ...rest] = s.split('.');
  const intPart = whole.replace(/^0+(?=\d)/, '').slice(0, 9);
  return rest.length ? `${intPart || '0'}.${rest.join('').slice(0, 2)}` : intPart;
};

// On blur the stored value becomes 2 decimals ("25000.5" -> "25000.50"); '' stays ''
export const canonicalAmount = (value) => {
  const v = String(value || '').replace(/\.$/, '');
  return v && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : v;
};

// Owner's display format: R#,###.##
export const formatRand = (value) => {
  const v = canonicalAmount(value);
  if (!v) return '';
  const [whole, cents] = v.split('.');
  return `R${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${cents}`;
};

// ---------------------------------------------------------------------------
// Field validation. Each validator returns '' when valid, else the message shown in red.
// "Required" messages are produced separately (see REQUIRED_FIELDS) so they only appear once
// the field has been visited or SEND has been clicked.
// ---------------------------------------------------------------------------
const validatePersonName = (value, label) => {
  const v = value.trim();
  if (!v) return '';
  if (!new RegExp(`^[${L} -]+$`).test(v)) return `${label} may only contain letters, spaces and hyphens.`;
  if (/^-|-$|- | -/.test(v)) return `${label}: a hyphen must join two names (e.g. Smith-Jones).`;
  if (v.replace(/[ -]/g, '').length < 3) return `${label} must be 3 or more characters.`;
  return '';
};

const validateDob = (form) => {
  if (!form.date_of_birth) return '';
  const dob = parseDate(form.date_of_birth);
  if (!dob) return 'Please select a valid date.';
  if (dob < DOB_FROM) return 'Please check the date of birth.';
  if (dob > today()) return 'Date of birth cannot be in the future.';
  // Cross-check against the SA ID number's date of birth (first 6 digits = YYMMDD)
  const idCheck = idTypeUsesSaIdNumber(form.id_type) ? validateSAIdNumber(form.id_number) : null;
  if (idCheck && idCheck.valid) {
    const id = form.id_number.replace(/\D/g, '');
    const fromId = id.slice(0, 6); // YYMMDD
    const fromDob = form.date_of_birth.slice(8, 10) + form.date_of_birth.slice(3, 5) + form.date_of_birth.slice(0, 2);
    if (fromId !== fromDob) {
      return `Date of birth does not match the ID number (the ID number shows ${idCheck.info.dateOfBirth}).`;
    }
  }
  return '';
};

const validateAppointment = (form) => {
  if (!form.date_of_appointment) return '';
  const d = parseDate(form.date_of_appointment);
  if (!d) return 'Please select a valid date.';
  if (d < APPOINTMENT_FROM || d > appointmentTo()) return 'Please check the date of appointment.';
  const dob = parseDate(form.date_of_birth);
  if (dob && d < dob) return 'Date of appointment cannot be before the date of birth.';
  return '';
};

const validateIdNumber = (form) => {
  const v = form.id_number.trim();
  if (!v) return '';
  if (idTypeUsesSaIdNumber(form.id_type)) return validateSAIdNumber(v).error;
  if (form.id_type === ID_TYPES.PASSPORT) {
    return /^[A-Z0-9]{6,15}$/.test(v) ? '' : 'Passport/Foreign ID number must be 6-15 letters and/or digits.';
  }
  // Asylum seeker's permit / file number
  if (!/^[A-Z0-9/-]{6,20}$/.test(v)) return 'Permit number must be 6-20 letters, digits, "/" or "-".';
  if (!/\d/.test(v)) return 'Permit number must contain digits.';
  return '';
};

// Safety net behind the formatters (which already strip these as they are typed or pasted)
const SARS_FORBIDDEN_RE = /[.]|[^\x20-\x7E]/;
const SARS_FORBIDDEN_MESSAGE = 'Full stops and accented letters are not allowed (SARS IRP5 rules).';

const validateJobTitle = (value) => {
  const v = value.trim();
  if (!v) return '';
  if (SARS_FORBIDDEN_RE.test(v)) return SARS_FORBIDDEN_MESSAGE;
  if (!new RegExp(`[${L}]`).test(v)) return 'Job title must contain letters.';
  if (v.length < 2) return 'Job title must be 2 or more characters.';
  return '';
};

const validateEmployerName = (value) => {
  const v = value.trim();
  if (!v) return '';
  if (SARS_FORBIDDEN_RE.test(v)) return SARS_FORBIDDEN_MESSAGE;
  if (!new RegExp(`[${L}]`).test(v)) return 'Name of employer must contain letters.';
  if (v.replace(/[^A-Za-z0-9]/g, '').length < 2) return 'Name of employer must be 2 or more characters.';
  return '';
};

const validateAddressText = (value, label, min = 2) => {
  const v = value.trim();
  if (!v) return '';
  if (SARS_FORBIDDEN_RE.test(v)) return SARS_FORBIDDEN_MESSAGE;
  if (!new RegExp(`[${L}]`).test(v)) return `${label} must contain letters.`;
  if (v.length < min) return `${label} must be ${min} or more characters.`;
  return '';
};

// Bank account messages that ask the client to double-check, rather than definitely wrong.
// They are shown in amber and don't block SEND (as on the Debit Order Mandate sites).
export const isSoftAccountWarning = (message) =>
  /typically|appears unusual/i.test(message || '');

export const FIELD_LABELS = {
  employer_name: 'Name of Employer',
  pay_frequency: 'Pay Frequency',
  first_names: 'First Names',
  last_name: 'Last Name',
  date_of_birth: 'Date of Birth',
  date_of_appointment: 'Date of Appointment',
  id_type: 'Identification Type',
  id_number: 'ID Number',
  passport_country: 'Passport Country Code',
  income_tax_number: 'Income Tax Number',
  job_title: 'Job Title',
  email: 'Email',
  cellphone: 'Cellphone No.',
  alt_cellphone: 'Alternative Cell Number',
  alt_email: 'Alternative Email Address',
  bank_name: 'Bank',
  account_number: 'Account Number',
  branch_code: 'Branch Code',
  account_type: 'Account Type',
  holder_relationship: 'Holder Relationship',
  unit_number: 'Unit Number',
  complex: 'Complex',
  street_number: 'Street Number',
  street: 'Street',
  suburb: 'Suburb or District',
  city: 'City or Town',
  postal_code: 'Postal Code',
  gross_salary: 'Gross Salary',
  uif_paye: 'Employee wants to pay UIF and PAYE?',
};

export const requiredFields = (form) => [
  'employer_name', 'pay_frequency', 'first_names', 'last_name', 'date_of_birth', 'date_of_appointment', 'id_type', 'id_number',
  ...(form.id_type === ID_TYPES.PASSPORT ? ['passport_country'] : []),
  'job_title', 'email', 'cellphone',
  // (branch code isn't listed: it is filled in from the bank, so "Bank is required" covers it)
  'bank_name', 'account_number', 'account_type', 'holder_relationship',
  'street_number', 'street', 'suburb', 'city', 'postal_code',
  // Remuneration Package: everything in it is mandatory (owner)
  'gross_salary', 'uif_paye',
];

// Returns { field: message } for every format problem (empty fields are not errors here).
export const formatErrors = (form) => {
  const errors = {
    employer_name: validateEmployerName(form.employer_name),
    first_names: validatePersonName(form.first_names, 'First names'),
    last_name: validatePersonName(form.last_name, 'Last name'),
    date_of_birth: validateDob(form),
    date_of_appointment: validateAppointment(form),
    id_number: validateIdNumber(form),
    income_tax_number: form.income_tax_number.trim() ? validateTaxNumber(form.income_tax_number).error : '',
    job_title: validateJobTitle(form.job_title),
    email: validateEmail(form.email.trim()),
    cellphone: validatePhoneNumber(form.cellphone, 'Cellphone number'),
    alt_cellphone: validatePhoneNumber(form.alt_cellphone, 'Alternative cell number'),
    alt_email: validateEmail(form.alt_email.trim()),
    account_number: form.account_number && !form.bank_name
      ? 'Please select the bank first so the account number can be checked.'
      : validateAccountNumber(form.account_number, form.bank_name),
    unit_number: form.unit_number.trim() && !/[A-Za-z0-9]/.test(form.unit_number) ? 'Please enter a unit number.' : '',
    complex: validateAddressText(form.complex, 'Complex'),
    street_number: form.street_number.trim() && !/\d/.test(form.street_number) ? 'Street number must contain a number.' : '',
    street: isOnlyStreetType(form.street)
      ? 'Please enter the street name as well, e.g. Main Road.'
      : validateAddressText(form.street, 'Street', 3),
    suburb: validateAddressText(form.suburb, 'Suburb or district'),
    city: validateAddressText(form.city, 'City or town'),
    postal_code: form.postal_code && !/^\d{4}$/.test(form.postal_code) ? 'Postal code must be 4 digits.' : '',
    gross_salary: form.gross_salary && !(Number(form.gross_salary) > 0) ? 'Gross salary must be more than R0.00.' : '',
  };
  if (form.alt_cellphone && !errors.alt_cellphone && form.alt_cellphone === form.cellphone) {
    errors.alt_cellphone = 'Please enter a different number from the main cellphone number.';
  }
  if (form.alt_email.trim() && !errors.alt_email && form.alt_email.trim().toLowerCase() === form.email.trim().toLowerCase()) {
    errors.alt_email = 'Please enter a different address from the main email address.';
  }
  Object.keys(errors).forEach((k) => { if (!errors[k]) delete errors[k]; });
  return errors;
};
